import { ref, computed } from 'vue'
import { useUserStore } from '@/stores/user'
import { apiClient } from '@/services/api'
import { googleAnalytics } from '@/services/googleAnalytics'

// Cookie categories
export interface CookiePreferences {
  essential: boolean // Always true
  functional: boolean
  analytics: boolean
  marketing: boolean
  timestamp?: Date
}

const STORAGE_KEY = 'cookie_consent'
const CONSENT_VERSION = '1.0' // Increment this when privacy policy changes

// Reactive state shared across all instances
const preferences = ref<CookiePreferences>({
  essential: true, // Always required
  functional: false,
  analytics: false,
  marketing: false
})

const hasConsented = ref(false)
const consentVersion = ref<string | null>(null)

/**
 * Composable for managing GDPR-compliant cookie consent
 *
 * Usage:
 * const { hasConsented, acceptAll, rejectAll, preferences } = useCookieConsent()
 */
export function useCookieConsent() {
  const userStore = useUserStore()

  /**
   * Load consent preferences from localStorage
   */
  const loadPreferences = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const data = JSON.parse(stored)

        // Check if consent is outdated (privacy policy changed)
        if (data.version !== CONSENT_VERSION) {
          console.log('Cookie consent version mismatch - requesting new consent')
          clearConsent()
          return
        }

        preferences.value = {
          essential: true, // Always true
          functional: data.functional ?? false,
          analytics: data.analytics ?? false,
          marketing: data.marketing ?? false,
          timestamp: data.timestamp ? new Date(data.timestamp) : undefined
        }

        hasConsented.value = true
        consentVersion.value = data.version

        // Apply preferences to tracking/analytics scripts
        applyPreferences()
      }
    } catch (error) {
      console.error('Failed to load cookie preferences:', error)
      clearConsent()
    }
  }

  /**
   * Save consent preferences to localStorage
   */
  const savePreferences = async (prefs: CookiePreferences) => {
    try {
      preferences.value = {
        ...prefs,
        essential: true, // Always true
        timestamp: new Date()
      }

      const data = {
        version: CONSENT_VERSION,
        essential: true,
        functional: prefs.functional,
        analytics: prefs.analytics,
        marketing: prefs.marketing,
        timestamp: new Date().toISOString()
      }

      localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
      hasConsented.value = true
      consentVersion.value = CONSENT_VERSION

      // Apply preferences to tracking/analytics scripts
      applyPreferences()

      // If user is logged in, save to backend
      if (userStore.isAuthenticated) {
        await saveToBackend(prefs)
      }

      console.log('Cookie preferences saved:', preferences.value)
    } catch (error) {
      console.error('Failed to save cookie preferences:', error)
    }
  }

  /**
   * Accept all cookies
   */
  const acceptAll = async () => {
    await savePreferences({
      essential: true,
      functional: true,
      analytics: true,
      marketing: true
    })
  }

  /**
   * Reject all non-essential cookies
   */
  const rejectAll = async () => {
    await savePreferences({
      essential: true,
      functional: false,
      analytics: false,
      marketing: false
    })
  }

  /**
   * Clear all consent data
   */
  const clearConsent = () => {
    localStorage.removeItem(STORAGE_KEY)
    hasConsented.value = false
    consentVersion.value = null
    preferences.value = {
      essential: true,
      functional: false,
      analytics: false,
      marketing: false
    }
  }

  /**
   * Apply cookie preferences to third-party scripts
   */
  const applyPreferences = () => {
    // Update Google Analytics consent
    googleAnalytics.updateConsent(
      preferences.value.analytics,
      preferences.value.marketing
    )

    // Log preferences for debugging
    if (preferences.value.analytics) {
      console.log('Analytics cookies enabled')
    } else {
      console.log('Analytics cookies disabled')
    }

    if (preferences.value.marketing) {
      console.log('Marketing cookies enabled')
    } else {
      console.log('Marketing cookies disabled')
    }

    if (preferences.value.functional) {
      console.log('Functional cookies enabled')
    } else {
      console.log('Functional cookies disabled - using defaults only')
    }
  }

  /**
   * Save preferences to backend (if user is logged in)
   */
  const saveToBackend = async (prefs: CookiePreferences) => {
    try {
      await apiClient.put('/users/cookie-consent', {
        functional: prefs.functional,
        analytics: prefs.analytics,
        marketing: prefs.marketing,
        version: CONSENT_VERSION
      })
      console.log('Cookie preferences saved to backend')
    } catch (error) {
      console.error('Failed to save cookie preferences to backend:', error)
      // Don't throw - localStorage is primary source of truth
    }
  }

  /**
   * Check if a specific cookie category is allowed
   */
  const canUseCookies = (category: keyof CookiePreferences): boolean => {
    // If user hasn't consented yet, only essential cookies are allowed
    if (!hasConsented.value) {
      return category === 'essential'
    }
    return preferences.value[category] === true
  }

  /**
   * Computed property for display purposes
   */
  const consentStatus = computed(() => {
    if (!hasConsented.value) return 'pending'

    const { functional, analytics, marketing } = preferences.value
    if (functional && analytics && marketing) return 'all'
    if (!functional && !analytics && !marketing) return 'essential-only'
    return 'custom'
  })

  return {
    // State
    preferences,
    hasConsented,
    consentVersion,
    consentStatus,

    // Actions
    loadPreferences,
    savePreferences,
    acceptAll,
    rejectAll,
    clearConsent,
    canUseCookies,

    // Constants
    CONSENT_VERSION
  }
}
